/**
 * Paleta dos gráficos (Chart.js / canvas) por tema — padrão único do software.
 *
 * No tema escuro, cinza médio/escuro é ilegível sobre o navy: eixos, títulos de
 * eixo, rótulos de valor e legendas vão para quase-branco (o mesmo tom do token
 * --color-text-primary do dark) e a grade é clareada. Use este helper em vez de
 * repetir os hex em cada componente — e nunca passe `var(--token)` para o
 * Chart.js, que desenha no canvas e não resolve variáveis CSS.
 */

export const chartColors = (isDark) => ({
    /** Ticks, títulos de eixo, rótulos de valor (datalabels) e legendas */
    axis: isDark ? '#F3F7FD' : '#334155',
    /** Textos de apoio dentro da área do gráfico (rótulos de quadrante, notas) */
    muted: isDark ? '#C7D4E8' : '#64748b',
    /** Linhas de grade */
    grid: isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(148, 163, 184, 0.15)',
    /** Linhas de referência (média da rede, crosshair, diagonal de paridade) */
    ref: isDark ? 'rgba(243, 247, 253, 0.6)' : '#64748b',
});

/**
 * Paleta das séries de barras da escala slate (prazos, histogramas, sensibilidade).
 *
 * No claro a escala slate escura funciona sobre o branco; no escuro o slate-900
 * (#0f172a) é praticamente o navy do fundo e a barra desaparece. A versão dark
 * sobe para azuis-aço, preservando a hierarquia da escala e mantendo contraste
 * com o fundo e com o rótulo branco desenhado dentro da barra.
 *
 * Contraste no escuro (fundo navy / rótulo branco interno):
 *   primaria    #3b6ea5 → 3,2:1 / 5,4:1
 *   secundaria  #7f8fa6 → 4,9:1 / 3,9:1
 *   apoioClaro  #8b9bb0 → 5,7:1 / 3,4:1
 *   apoioMedio  #5b7ba3 → 3,8:1 / 4,4:1
 */
export const chartSeriesColors = (isDark) => ({
    /** Série principal / total concedido (PMP Fábrica, PMR Loja, histogramas) */
    primaria:   isDark ? '#3b6ea5' : '#0f172a',
    /** Série de resultado comparada à principal (PMP Real, PMR Real) */
    secundaria: isDark ? '#7f8fa6' : '#475569',
    /** Segmento de custo/atraso mais claro (Transit Time, Atraso de recebimento) */
    apoioClaro: isDark ? '#8b9bb0' : '#64748b',
    /** Segmento de custo/atraso intermediário (Atraso Operacional) */
    apoioMedio: isDark ? '#5b7ba3' : '#334155',
});

/**
 * Aplica a paleta nos defaults do Chart.js — rede de segurança para gráficos que
 * não declaram cor de tick/título/legenda explicitamente.
 *
 * O Chart.js resolve os defaults no momento do desenho, então os gráficos que já
 * estão montados (e que não são recriados na troca de tema) precisam de um
 * `update` para repintar com a nova paleta.
 *
 * O `import` é dinâmico de propósito: quem chama isto é o ThemeProvider, que está
 * no bundle inicial — um import estático arrastaria o Chart.js para lá, fora dos
 * chunks lazy das páginas. Quando existe gráfico na tela, a lib já está em cache.
 */
export const applyChartThemeDefaults = async (isDark) => {
    const { Chart } = await import('chart.js');
    const c = chartColors(isDark);
    Chart.defaults.color = c.axis;
    Chart.defaults.borderColor = c.grid;
    const live = Chart.instances ? Object.values(Chart.instances) : [];
    live.forEach((inst) => {
        if (inst && typeof inst.update === 'function') inst.update('none');
    });
};
